import { Onboarding } from "@redotech/account-common/onboarding";
import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useLoad } from "@redotech/react-util/load";
import { CurrencyConverterContext } from "@redotech/react-util/money";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import {
  pricePerOrder as calcPricePerOrder,
  derivePaymentModel,
} from "@redotech/redo-return-coverage-common/coverage";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { formatDate } from "@redotech/redo-web/time";
import { memo, useContext } from "react";
import { TeamContext } from "../../../team";
import * as teamCss from "../../../team.module.css";
import { ReturnsCoverageForm } from "../returns/coverage";

export const OnboardingStats = memo(function OnboardingStats({
  onboarding,
  input,
}: {
  onboarding?: Onboarding | undefined;
  input: ReturnsCoverageForm;
}) {
  const team = useContext(TeamContext);
  const currencyConverter = useContext(CurrencyConverterContext);
  const shopCurrency = getShopCurrencyWithDefault({ team });

  const exchangeRatesLoad = useLoad(async () => {
    if (shopCurrency === Currency.USD) {
      return null;
    }
    return await currencyConverter();
  }, [shopCurrency]);

  const PERCENT_FORMAT = new Intl.NumberFormat(undefined, {
    style: "percent",
    maximumFractionDigits: 0,
  });

  const calcBase =
    onboarding?.orders &&
    ((2 * onboarding.orders.realRefundPercentage) / 100) *
      +onboarding.labelPrice;

  const derivedPaymentModel = derivePaymentModel(
    input.value.merchantPaidEnabledDates.map((d) => ({
      start: d.start?.toISOString() ?? null,
      end: d.end?.toISOString() ?? null,
    })),
  );

  const getRatio = (
    params: { exchange?: boolean; refund?: boolean; credit?: boolean } = {},
  ) => {
    return calcPricePerOrder(
      {
        exchange:
          params.exchange !== undefined
            ? params.exchange
            : input.value.coverage.exchange,
        refund:
          params.refund !== undefined
            ? params.refund
            : input.value.coverage.refund,
        storeCredit:
          params.credit !== undefined
            ? params.credit
            : input.value.coverage.credit,
      },
      derivedPaymentModel,
      { amount: 1, currency: Currency.USD },
    ).amount;
  };

  const ratio = getRatio();

  return (
    <div className={gridCss.span12}>
      <dl className={teamCss.table}>
        <dt className={teamCss.tableHeader}>Start</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            formatDate(Temporal.Instant.from(onboarding.orders.fromDate))}
        </dd>
        <dt className={teamCss.tableHeader}>End</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            formatDate(Temporal.Instant.from(onboarding.orders.toDate))}
        </dd>
        <dt className={teamCss.tableHeader}>Orders</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            new Intl.NumberFormat().format(onboarding.orders.count)}
        </dd>
        <dt className={teamCss.tableHeader}>Refunded orders</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            new Intl.NumberFormat().format(onboarding.orders.countRefunded)}
        </dd>
        <dt className={teamCss.tableHeader}>Refund rate</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            PERCENT_FORMAT.format(onboarding.orders.refundPercentage / 100)}
        </dd>
        <dt className={teamCss.tableHeader}>Estimated return rate</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.orders &&
            PERCENT_FORMAT.format(onboarding.orders.realRefundPercentage / 100)}
        </dd>
        <dt className={teamCss.tableHeader}>Price per label</dt>
        <dd className={teamCss.tableCell}>
          {onboarding?.labelPrice && !isNaN(+onboarding.labelPrice)
            ? moneyFormat({
                amount: +onboarding.labelPrice,
                currency: Currency.USD,
              })
            : "N/A"}
        </dd>
        <dt className={teamCss.tableHeader}>Calculated base</dt>
        <dd className={teamCss.tableCell}>
          {calcBase !== undefined && !isNaN(calcBase)
            ? moneyFormat({ amount: calcBase, currency: Currency.USD })
            : "N/A"}
        </dd>
        <dt className={teamCss.tableHeader}>Calculated price</dt>
        <dd className={teamCss.tableCell}>
          {calcBase !== undefined && !isNaN(calcBase)
            ? moneyFormat({ amount: ratio * calcBase, currency: Currency.USD })
            : "N/A"}
        </dd>
        {exchangeRatesLoad.value && (
          <>
            <dt className={teamCss.tableHeader}>
              Calculated price in {shopCurrency}{" "}
            </dt>
            <dd className={teamCss.tableCell}>
              {calcBase !== undefined &&
                moneyFormat(
                  exchangeRatesLoad.value.convert(
                    { amount: ratio * calcBase, currency: Currency.USD },
                    shopCurrency,
                  ),
                )}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
});
